import { apiPost, apiPostPublic, apiGet } from '../api';

// Backend-compatible payload (matches ContactFormSchema in backend/src/schemas/index.ts)
export interface ContactFormPayload {
  name: string;
  email: string;
  subject?: string;  // Maps from frontend 'reason' field
  message: string;
  turnstile_token?: string;  // Snake case to match backend
  // Honeypot (#3168). The signed-out NATIVE contact path submits this always
  // empty; a non-empty value is treated as a bot and rejected server-side
  // (NativeAnonContactFormSchema). Web's honeypot is client-side only and is
  // never sent, so this stays optional and unused on the web path.
  website?: string;
}

// Authed contact path (#2212 T3/T4, native contact form): identity comes
// from the session server-side — only subject + message are sent. Matches
// AuthedContactFormSchema in backend/src/schemas/index.ts.
export interface AuthedContactPayload {
  subject?: string;
  message: string;
}

// Mirrors the backend route's success envelope (`return c.json({ success,
// message, submitted_at })` in backend/src/routes/contact.ts) — shared by
// both the anonymous and authed paths.
export interface ContactResponse {
  success: boolean;
  message: string;
  submitted_at: number;
}

// Platform-admin read surface for stored contact submissions (#2955).
// Mirrors the GET /contact/admin/messages handler envelope in
// backend/src/routes/contact.ts. Every row is PII (submitter name/email/
// message) returned to platform admins only; the backend marks the response
// `Cache-Control: private, no-store` (Rule 54).
export type ContactMessageStatus = 'new' | 'in_progress' | 'resolved' | 'spam';

export interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string | null;
  message: string;
  submitted_at: number;
  ip_address: string | null;
  user_agent: string | null;
  status: ContactMessageStatus;
  created_at: number;
  updated_at: number;
}

export interface ContactMessagesResponse {
  messages: ContactMessage[];
  count: number;
  limit: number;
  offset: number;
}

export const contactApi = {
  submitMessage: (data: ContactFormPayload): Promise<ContactResponse> =>
    apiPost('/contact', data),
  // Native transport attaches the Bearer session; the backend waives
  // Turnstile for a valid session (#2212 T3).
  submitAuthedMessage: (data: AuthedContactPayload): Promise<ContactResponse> =>
    apiPost('/contact', data),
  /**
   * Signed-out ANONYMOUS submission (#3168). Uses `apiPostPublic` so the request
   * carries NO session — native must not attach a stored Bearer even when one
   * lingers in SecureStore (that would silently route the "anonymous" form to the
   * backend's authed branch); web sends no cookie. The backend's signed-out
   * native branch (`X-Client: native`, no session) waives Turnstile and relies on
   * a honeypot + per-IP rate limit. Consumed by `useAnonContactSubmit`.
   */
  submitAnonMessage: (data: ContactFormPayload): Promise<ContactResponse> =>
    apiPostPublic('/contact', data),

  /**
   * List stored contact submissions (platform admin only).
   * GET /contact/admin/messages
   */
  listMessages: (params?: {
    status?: ContactMessageStatus;
    limit?: number;
    offset?: number;
  }): Promise<ContactMessagesResponse> => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    // `!= null`, not truthiness: `offset: 0` (the first page) and `limit: 0`
    // are legitimate values that a truthy check silently drops.
    if (params?.limit != null) query.append('limit', params.limit.toString());
    if (params?.offset != null) query.append('offset', params.offset.toString());
    const qs = query.toString();
    return apiGet<ContactMessagesResponse>(
      `/contact/admin/messages${qs ? `?${qs}` : ''}`,
    );
  },
};